import { CellValueChangedEvent } from 'ag-grid-community';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { AgGridTable } from 'core/components';
import { NotificationPosition, TableLayoutType } from 'core/enums';
import { AgGridClientTableProvider } from 'core/states';
import { ISelectionActionProps } from 'core/types';
import { useGetProjectUsers, useProjectUserActions } from 'projects/hooks';
import { IProjectUser } from 'projects/types';

import { RemoveUserButton, UpsertUserButton } from './actions';
import { getColumnDefs } from './columns';

interface IProps {
  projectId: number;
}

export const UserList = ({ projectId }: IProps) => {
  const { t } = useTranslation();

  const { records, isLoading, loadingPercentage, currentPage } = useGetProjectUsers({
    project: projectId,
  });

  const { upsertProjectUser } = useProjectUserActions(NotificationPosition.TOP);

  const columnDefs = useMemo(() => getColumnDefs(projectId), [projectId]);

  const onCellValueChanged = useCallback(
    async (_, { colDef: { field }, data, newValue }: CellValueChangedEvent) => {
      const { success, error, user } = await upsertProjectUser({
        projectCorporateUser: {
          projectId,
          [field]: newValue ? 1 : 0,
          corporateUserId: data.id,
        },
      });

      if (!success) {
        throw new Error(error);
      }

      return { updatedRow: user };
    },
    [projectId, upsertProjectUser]
  );

  const actionButtons = useCallback(() => {
    return [<UpsertUserButton key="addUserButton" projectId={projectId} />];
  }, [projectId]);

  const selectionActionButtons = useCallback(
    ({ selectedRows }: ISelectionActionProps) => {
      return [
        <RemoveUserButton
          key="removeUserButton"
          projectId={projectId}
          users={selectedRows as IProjectUser[]}
          text={t('buttonLabelRemove')}
        />,
      ];
    },
    [projectId, t]
  );

  return (
    <AgGridClientTableProvider
      tableId={'projectUserList'}
      entityType="User"
      columnDefs={columnDefs}
      data={records}
      isDataLoading={isLoading}
      loadingPercentage={loadingPercentage}
      dataCurrentPage={currentPage}
      onCellValueChanged={onCellValueChanged}
      layoutType={TableLayoutType.PROJECT_USER}
      isRowSelectable={({ data }) => !data.isOwner}
      headerCheckbox
      hasLayoutActions
    >
      <AgGridTable actionButtons={actionButtons} selectionActionButtons={selectionActionButtons} />
    </AgGridClientTableProvider>
  );
};
